import React, { useEffect, useState } from 'react';

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.56 2.68-3.86 2.68-6.62Z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
    />
    <path
      fill="#FBBC05"
      d="M3.97 10.72A5.4 5.4 0 0 1 3.69 9c0-.6.1-1.18.28-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.05l3.01-2.33Z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.32 0 2.5.45 3.43 1.33l2.57-2.57C13.46.9 11.42 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
    />
  </svg>
);

const AppleMark = ({ className = 'h-[18px] w-[18px]' }) => (
  <svg
    viewBox="0 0 18 18"
    aria-hidden="true"
    className={className}
    fill="currentColor"
  >
    <path d="M13.39 9.2c-.02-2.07 1.69-3.06 1.76-3.11-.96-1.41-2.45-1.6-2.98-1.62-1.27-.13-2.48.75-3.12.75-.65 0-1.65-.73-2.7-.71-1.39.02-2.68.82-3.4 2.07-1.45 2.52-.37 6.25 1.04 8.3.69 1 1.52 2.13 2.6 2.09 1.04-.04 1.43-.68 2.69-.68s1.61.68 2.71.66c1.12-.02 1.84-1.02 2.53-2.03.79-1.16 1.12-2.29 1.14-2.35-.03-.01-2.18-.84-2.2-3.36Zm-2.39-5.56c.57-.7.96-1.67.85-2.64-.83.03-1.82.55-2.42 1.25-.53.62-1 1.61-.87 2.56.92.07 1.86-.47 2.44-1.17Z" />
  </svg>
);

const AuthGateStep = ({
  isLoading = false,
  error = '',
  info = '',
  onApple,
  onGoogle,
  onEmailCreate,
  onEmailSignIn,
  onEmailLinkSend,
  onEmailLinkComplete,
  onClearError,
  initialMode = 'create',
  initialEmail = '',
  emailAuthMode = 'password',
  showSocialAuth = true,
  createTitle = 'Create your account',
  signInTitle = 'Sign in to continue',
  subtitle = 'Sign in with Apple, Google, or email to start making your pet\'s baby videos.',
}) => {
  const [mode, setMode] = useState(initialMode === 'sign_in' ? 'sign_in' : 'create');
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const isPasswordMode = emailAuthMode === 'password';
  const isEmailLinkSendMode = emailAuthMode === 'magic_link_send';
  const isEmailLinkCompleteMode = emailAuthMode === 'magic_link_complete';

  const normalizedEmail = String(email || '').trim();
  const canSubmitEmail = isPasswordMode
    ? (Boolean(normalizedEmail) && Boolean(password) && !isLoading)
    : (Boolean(normalizedEmail) && !isLoading);
  const activeError = localError || error;
  const isCreateMode = mode === 'create';

  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail]);

  const validateInputs = () => {
    if (!normalizedEmail) {
      setLocalError('Please enter your email address.');
      return false;
    }
    if (isPasswordMode && !password) {
      setLocalError('Please enter your password.');
      return false;
    }
    return true;
  };

  const handleSocialSubmit = async (callback) => {
    if (typeof callback !== 'function') {
      return;
    }

    setLocalError('');
    if (typeof onClearError === 'function') {
      onClearError();
    }
    await callback();
  };

  const handlePrimarySubmit = async () => {
    if (!validateInputs()) {
      return;
    }
    setLocalError('');
    if (typeof onClearError === 'function') {
      onClearError();
    }
    if (isEmailLinkSendMode) {
      if (typeof onEmailLinkSend !== 'function') {
        setLocalError('Email sign-in is unavailable right now.');
        return;
      }
      await onEmailLinkSend({
        email: normalizedEmail,
      });
      return;
    }
    if (isEmailLinkCompleteMode) {
      if (typeof onEmailLinkComplete !== 'function') {
        setLocalError('Email sign-in is unavailable right now.');
        return;
      }
      await onEmailLinkComplete({
        email: normalizedEmail,
      });
      return;
    }
    if (isCreateMode) {
      await onEmailCreate({
        email: normalizedEmail,
        password,
      });
      return;
    }

    await onEmailSignIn({
      email: normalizedEmail,
      password,
    });
  };

  const handleModeSwitch = (nextMode) => {
    setMode(nextMode);
    setLocalError('');
    if (typeof onClearError === 'function') {
      onClearError();
    }
  };

  return (
    <div className="max-w-2xl mx-auto overflow-x-hidden">
      <div className="text-center mb-5 lg:mb-6">
        <h2 className="text-3xl sm:text-4xl font-bold text-purple-700 mb-2">
          {isCreateMode ? createTitle : signInTitle}
        </h2>
        <p className="text-base lg:text-lg text-slate-600">
          {subtitle}
        </p>
      </div>

      <div className="bg-white rounded-3xl p-6 sm:p-8 lg:p-10 shadow-lg">
        {showSocialAuth ? (
          <>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => handleSocialSubmit(onApple)}
                disabled={isLoading || typeof onApple !== 'function'}
                className={`w-full flex items-center justify-center gap-3 rounded-full border px-5 py-4 text-base lg:text-lg font-semibold transition-colors ${
                  isLoading || typeof onApple !== 'function'
                    ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                    : 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                <AppleMark className="h-[18px] w-[18px] shrink-0" />
                <span>Sign in with Apple</span>
              </button>

              <button
                type="button"
                onClick={() => handleSocialSubmit(onGoogle)}
                disabled={isLoading || typeof onGoogle !== 'function'}
                className={`w-full flex items-center justify-center gap-3 rounded-full border px-5 py-4 text-base lg:text-lg font-semibold transition-colors ${
                  isLoading || typeof onGoogle !== 'function'
                    ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                    : 'border-purple-200 bg-white text-slate-800 hover:border-pink-200 hover:bg-pink-50/40'
                }`}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm border border-slate-200">
                  <GoogleMark />
                </span>
                <span>Sign in with Google</span>
              </button>
            </div>

            <div className="my-6 flex items-center gap-4">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-sm font-medium uppercase tracking-[0.2em] text-slate-400">or</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
          </>
        ) : null}

        <div className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setLocalError('');
              if (typeof onClearError === 'function') {
                onClearError();
              }
            }}
            autoComplete="email"
            placeholder="Email address"
            disabled={isLoading}
            className="w-full border border-purple-200 rounded-2xl px-5 py-4 text-lg lg:text-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-200"
          />
          {isPasswordMode ? (
            <input
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setLocalError('');
                if (typeof onClearError === 'function') {
                  onClearError();
                }
              }}
              autoComplete={isCreateMode ? 'new-password' : 'current-password'}
              placeholder={isCreateMode ? 'Create a password' : 'Password'}
              disabled={isLoading}
              className="w-full border border-purple-200 rounded-2xl px-5 py-4 text-lg lg:text-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          ) : null}
        </div>

        {info ? (
          <p className="mt-3 text-sm lg:text-base text-slate-600">{info}</p>
        ) : null}

        {activeError ? (
          <p className="mt-3 text-base lg:text-lg text-red-600">{activeError}</p>
        ) : null}

        <div className="mt-5">
          <button
            type="button"
            onClick={handlePrimarySubmit}
            disabled={!canSubmitEmail}
            className={`w-full text-base lg:text-lg font-bold py-3 px-6 rounded-full shadow-lg transition-all duration-300 ${
              canSubmitEmail
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white cursor-pointer'
                : 'bg-purple-100 text-purple-400 cursor-not-allowed shadow-none'
            }`}
          >
            {isLoading
              ? 'Please wait...'
              : (
                isEmailLinkSendMode
                  ? 'Email me a sign-in link'
                  : isEmailLinkCompleteMode
                    ? 'Continue email sign-in'
                    : (isCreateMode ? 'Continue with email' : 'Sign in with email')
              )}
          </button>
        </div>

        {isPasswordMode ? (
          <div className="mt-5 text-center">
            <p className="text-sm lg:text-base text-slate-500">
              {isCreateMode ? 'Already have an account?' : 'Need to create an account?'}
            </p>
            <button
              type="button"
              onClick={() => handleModeSwitch(isCreateMode ? 'sign_in' : 'create')}
              disabled={isLoading}
              className="mt-2 inline-flex items-center justify-center rounded-full px-4 py-2 text-sm lg:text-base font-semibold text-purple-700 transition-colors hover:bg-purple-50 disabled:cursor-not-allowed disabled:text-purple-300"
            >
              {isCreateMode ? 'Sign in instead' : 'Create one instead'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AuthGateStep;
